import React from "react";
import { Easing, interpolate, random, spring, useCurrentFrame } from "remotion";
import { theme } from "../../brand";
import { LOOK } from "../../b2c/look";
import { euros } from "../../merchant/copy";
import { type Merchant, MERCHANTS } from "../../merchant/merchants";
import { LogoTile } from "../../merchant/ui/Logo";
import { REEL, ReelFrame } from "../ui/ReelFrame";
import { LOULENN, LOULENN_REWARDS } from "./copy";

/**
 * Reel "Loulenn" (9:16, silent, ~9.4s): the brand's first successful
 * referral, in three hits.
 *   1. Cover (frame 0 is complete): a huge "Premier parrainage réussi",
 *      the Loulenn logo with a check, "pour Loulenn". At 0,5 s the check
 *      and the accent word pop, confetti, a flash and a small shake.
 *   2. The title and the logo shrink to the top; two cards slam in from
 *      the sides. The amount of each explodes in on impact (overshoot,
 *      count-up, shake, flash, confetti): 30 € the parrain, then 10 € the
 *      filleul.
 *   3. End card: the usual Frak CTA.
 *
 * Name, logo and colours come from the brand snapshot (`slug: loulenn`).
 * Silent: the sound is added in Instagram. Text stays in the Reels-safe
 * band (y 220–1420, 180 px clear on the right); the confetti and the
 * flash are drawn under every text.
 */

/** Timeline, in frames. Holds are reading time: never shorten one. */
const T = {
  /** Check + accent pop, confetti, flash. */
  burst: 14,
  /** Title and logo move up, making room for the cards. */
  settle: 36,
  /** A card starts sliding in; it lands `lag` frames later (the impact). */
  referrer: 48,
  referee: 90,
  lag: 8,
  count: 14,
  exit: 184,
  end: 196,
  duration: 280,
} as const;

export const LOULENN_DURATION = T.duration;

const HIT = [T.referrer + T.lag, T.referee + T.lag] as const;
const SLUG = "loulenn";
const GREEN = theme.brand.colors.success[800];
const MINT = theme.brand.colors.success[300];
const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ramp = (frame: number, at: number, len: number, ease = Easing.out(Easing.cubic)) =>
  interpolate(frame, [at, at + len], [0, 1], { ...clamp, easing: ease });
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Layout (logical px, 1080×1920). */
const L = {
  title: { top: 230, left: 110, width: 820, size: 132, end: 0.58 },
  logo: { size: 420, top: 780, end: { top: 540, scale: 0.55 } },
  card: { left: 150, width: 780, height: 290, tops: [802, 1114] },
} as const;
const CX = REEL.width / 2;
const LOGO_CENTER = L.logo.top + L.logo.size / 2;

/** Impacts that shake the scene (px, decaying over 9 frames) and flash it. */
const SHAKES = [
  { at: T.burst, amp: 8 },
  { at: HIT[0], amp: 18 },
  { at: HIT[1], amp: 13 },
];
const shake = (frame: number) =>
  SHAKES.reduce(
    (s, { at, amp }) => {
      const t = frame - at;
      if (t < 0 || t > 9) return s;
      const k = amp * (1 - t / 9);
      return { x: s.x + Math.sin(t * 2.9) * k, y: s.y + Math.cos(t * 3.7) * k * 0.7 };
    },
    { x: 0, y: 0 },
  );
const flashAt = (frame: number) =>
  SHAKES.reduce((m, { at }) => {
    const t = frame - at;
    return t >= 0 && t < 7 ? Math.max(m, 0.26 * (1 - t / 7)) : m;
  }, 0);

/** Confetti from a point. Deterministic (`random`), drawn under the text. */
const Burst: React.FC<{
  x: number;
  y: number;
  at: number;
  seed: string;
  count: number;
  colors: string[];
  /** Angle range in turns of π (0 = right, 0.5 = down, 1 = left). */
  spread?: readonly [number, number];
}> = ({ x, y, at, seed, count, colors, spread = [-1, 1] }) => {
  const frame = useCurrentFrame() - at;
  if (frame < 0 || frame > 64) return null;
  const t = Math.min(1, frame / 44);
  const out = 1 - (1 - t) * (1 - t);
  return (
    <>
      {Array.from({ length: count }, (_, i) => {
        const angle = lerp(spread[0], spread[1], random(`${seed}a${i}`)) * Math.PI;
        const power = 260 + random(`${seed}p${i}`) * 340;
        const size = 14 + random(`${seed}s${i}`) * 16;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size * (i % 2 ? 1 : 0.5),
              borderRadius: 3,
              background: colors[i % colors.length],
              translate: `${Math.cos(angle) * power * out}px ${
                Math.sin(angle) * power * out + 220 * t * t
              }px`,
              rotate: `${random(`${seed}r${i}`) * 900 * t}deg`,
              opacity: interpolate(frame, [38, 64], [1, 0], clamp),
            }}
          />
        );
      })}
    </>
  );
};

const Check: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path
      d="M4 12.5l5 5L20 6.5"
      fill="none"
      stroke={LOOK.white}
      strokeWidth={3.4}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Cover: the headline, one word per line, then "pour {name}". */
const Title: React.FC<{ name: string; p: number }> = ({ name, p }) => {
  const frame = useCurrentFrame();
  const words = LOULENN.hook.split(" ");
  const pop = Math.sin(Math.PI * ramp(frame, T.burst - 4, 14));
  return (
    <div
      style={{
        position: "absolute",
        top: L.title.top,
        left: L.title.left,
        width: L.title.width,
        transformOrigin: "50% 0",
        scale: lerp(1, L.title.end, p),
        textAlign: "center",
        fontFamily: LOOK.display,
        fontWeight: LOOK.bold,
        color: LOOK.white,
      }}
    >
      {words.map((w, i) => {
        const last = i === words.length - 1;
        return (
          <div
            key={w}
            style={{
              fontSize: L.title.size,
              lineHeight: 1,
              letterSpacing: -L.title.size * 0.04,
              color: last ? MINT : LOOK.white,
              scale: last ? 1 + 0.2 * pop : 1,
              rotate: last ? `${-3 * pop}deg` : undefined,
            }}
          >
            {w}
          </div>
        );
      })}
      <div
        style={{
          marginTop: 14,
          fontSize: 76,
          lineHeight: 1,
          letterSpacing: -2,
          fontWeight: LOOK.semi,
          color: LOOK.sky,
        }}
      >
        {LOULENN.forBrand.replace("{name}", name)}
      </div>
    </div>
  );
};

/** The brand's logo with a "done" check; shrinks to the top with the title. */
const Logo: React.FC<{ m: Merchant; p: number }> = ({ m, p }) => {
  const frame = useCurrentFrame();
  const bump = Math.sin(Math.PI * ramp(frame, T.burst, 12));
  const check = Math.sin(Math.PI * ramp(frame, T.burst, 16));
  const { size } = L.logo;
  return (
    <div
      style={{
        position: "absolute",
        left: CX - size / 2,
        top: lerp(L.logo.top, L.logo.end.top, p),
        width: size,
        height: size,
        transformOrigin: "50% 0",
        scale: lerp(1, L.logo.end.scale, p),
        translate: `0 ${Math.sin(frame / 9) * 7 * (1 - p)}px`,
      }}
    >
      <div style={{ scale: 1 + 0.1 * bump }}>
        <LogoTile m={m} size={size} />
      </div>
      <div
        style={{
          position: "absolute",
          right: -36,
          bottom: -36,
          width: 132,
          height: 132,
          borderRadius: 66,
          background: LOOK.green,
          border: `9px solid ${LOOK.white}`,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          scale: 1 + 0.3 * check,
          boxShadow: "0 16px 32px -8px rgba(0,0,0,0.4)",
        }}
      >
        <Check size={72} />
      </div>
    </div>
  );
};

/** A reward card: slams in from a side, then its amount explodes in. */
const RewardCard: React.FC<{
  label: string;
  amount: number;
  at: number;
  top: number;
  from: 1 | -1;
  tone: "light" | "green";
}> = ({ label, amount, at, top, from, tone }) => {
  const frame = useCurrentFrame();
  const hit = at + T.lag;
  const slide = spring({
    frame: frame - at,
    fps: REEL.fps,
    config: { damping: 15, stiffness: 190, mass: 0.7 },
  });
  const punch = spring({
    frame: frame - hit,
    fps: REEL.fps,
    config: { damping: 7, stiffness: 240, mass: 0.5 },
  });
  const n = Math.round(
    amount * ramp(frame, hit, T.count, Easing.out(Easing.cubic)),
  );
  const light = tone === "light";
  return (
    <div
      style={{
        position: "absolute",
        left: L.card.left,
        top,
        width: L.card.width,
        height: L.card.height,
        borderRadius: 44,
        background: light ? LOOK.white : GREEN,
        boxShadow: "0 40px 80px -20px rgba(0, 20, 90, 0.5)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        translate: `${(1 - slide) * 1200 * from}px 0`,
        rotate: `${(1 - slide) * 6 * from}deg`,
        opacity: frame < at ? 0 : 1,
      }}
    >
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 50,
          letterSpacing: -1,
          lineHeight: 1,
          color: light ? LOOK.ink : LOOK.greenSoft,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 10,
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 172,
          letterSpacing: -6,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
          color: light ? GREEN : LOOK.white,
          opacity: frame < hit ? 0 : 1,
          scale: frame < hit ? 1 : 1 + 0.3 * (1 - punch),
        }}
      >
        {euros("fr", n)}
      </div>
    </div>
  );
};

export const LoulennReel: React.FC = () => {
  // Thrown here, not at import: the bundle holds every video.
  const m = MERCHANTS.find((x) => x.slug === SLUG);
  if (!m) {
    throw new Error(
      `Loulenn reel: brand "${SLUG}" is not in the snapshot. Run \`npm run sync:brands\`.`,
    );
  }
  const frame = useCurrentFrame();
  const p = spring({
    frame: frame - T.settle,
    fps: REEL.fps,
    config: { damping: 16, stiffness: 170, mass: 0.8 },
  });
  const out = ramp(frame, T.exit, 10, Easing.in(Easing.quad));
  const s = shake(frame);
  const flash = flashAt(frame);
  const cardY = (i: number) => L.card.tops[i] + L.card.height / 2;
  const party = [m.colors.brand, LOOK.white, LOOK.sky, MINT];

  return (
    <ReelFrame
      exitAt={T.exit}
      end={{ at: T.end, line: LOULENN.end.line, sub: LOULENN.end.sub }}
    >
      {/* Flash: under every text, so it never washes one out. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: LOOK.white,
          opacity: flash * (1 - out),
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 1 - out,
          scale: 1 - 0.05 * out,
          translate: `${s.x}px ${s.y}px`,
        }}
      >
        {/* Confetti first: everything else is drawn over it. */}
        <Burst x={CX} y={LOGO_CENTER} at={T.burst} seed="logo" count={30} colors={party} spread={[-0.1, 1.1]} />
        <Burst x={CX} y={cardY(0)} at={HIT[0]} seed="c1" count={26} colors={party} />
        <Burst x={CX} y={cardY(1)} at={HIT[1]} seed="c2" count={30} colors={party} />
        <Logo m={m} p={p} />
        <Title name={m.name} p={p} />
        <RewardCard
          label={LOULENN.referrer}
          amount={LOULENN_REWARDS.referrer}
          at={T.referrer}
          top={L.card.tops[0]}
          from={-1}
          tone="light"
        />
        <RewardCard
          label={LOULENN.referee}
          amount={LOULENN_REWARDS.referee}
          at={T.referee}
          top={L.card.tops[1]}
          from={1}
          tone="green"
        />
      </div>
    </ReelFrame>
  );
};
